import * as Sentry from '@sentry/react'
import { onIdTokenChanged, type User } from 'firebase/auth'
import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react'
import { Logger } from '@/components/Logger'
import { LocalStorageKeys } from '@/constants/localstorage'
import { queryClient } from '@/services/api'
import { firebaseAuth } from '@/services/firebaseApp'

type FirebaseUser = User | null | undefined
type ContextState = { user: FirebaseUser }

const FirebaseAuthContext = createContext<ContextState | undefined>(undefined)

const FirebaseAuthProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<FirebaseUser>(undefined)
  const [, setUserVersion] = useState(0)
  const value = { user }

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(firebaseAuth, (firebaseUser) => {
      Logger.log('Firebase authentication state changed', { signedIn: Boolean(firebaseUser) })

      if (firebaseUser) {
        setUser(firebaseUser)
        setUserVersion((value) => value + 1)

        Sentry.setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email ?? undefined,
          username: firebaseUser.displayName ?? undefined,
        })

        firebaseUser
          .getIdToken()
          .then((token) => {
            if (firebaseAuth.currentUser !== firebaseUser) return
            sessionStorage.setItem(
              LocalStorageKeys.SENTRY_USER,
              firebaseUser.email ?? firebaseUser.uid,
            )
            Sentry.setContext('user', {
              name: firebaseUser.displayName,
              email: firebaseUser.email,
              uid: firebaseUser.uid,
            })
            sessionStorage.setItem(LocalStorageKeys.FIREBASE_TOKEN, token)
          })
          .catch((error) => {
            Logger.error('Failed to fetch Firebase ID token', error)
          })
      } else {
        setUser(null)
        setUserVersion((value) => value + 1)
        queryClient.clear()
        Sentry.setUser(null)
        sessionStorage.removeItem(LocalStorageKeys.SENTRY_USER)
        sessionStorage.removeItem(LocalStorageKeys.FIREBASE_TOKEN)
      }
    })

    return () => unsubscribe()
  }, [])
  if (user === undefined) return null

  return <FirebaseAuthContext.Provider value={value}>{children}</FirebaseAuthContext.Provider>
}

function useFirebaseAuth() {
  const context = useContext(FirebaseAuthContext)
  if (context === undefined) {
    throw new Error('useFirebaseAuth must be used within a FirebaseAuthProvider')
  }
  return context.user
}

export { FirebaseAuthProvider, useFirebaseAuth }
